import { useState } from 'react';
import ConfirmDialog from './ConfirmDialog';
import { toast } from 'sonner';
import {
  UploadSimple, FolderSimplePlus, PencilSimple, ArrowsOutCardinal, ShareNetwork,
  TrashSimple, PushPin, CaretRight, House,
} from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { dmsApi } from './dmsApi';
import { AccessBadge, folderPath } from './docsUi';

const Action = ({ icon: Icon, label, onClick, testId, tone }) => (
  <Button variant={tone === 'primary' ? 'default' : 'outline'} size="sm" onClick={onClick}
    data-testid={testId}
    className={`h-9 text-sm ${tone === 'primary' ? 'bg-teal-700 hover:bg-teal-800'
      : tone === 'danger' ? 'border-zinc-200 text-rose-700 hover:bg-rose-50' : 'border-zinc-200 text-zinc-700'}`}>
    <Icon size={14} className="mr-1.5" />{label}
  </Button>
);

/** フォルダーの現在位置（パンくず）。ページ見出しの上に置き、見出しは現在のフォルダー名が担います。 */
export const FolderBreadcrumb = ({ folder, folders, onOpenFolder }) => {
  const path = folder ? folderPath(folders, folder.id) : [];
  return (
    <nav className="flex items-center gap-1 flex-wrap text-sm text-zinc-500" data-testid="dms-breadcrumb">
      <button type="button" onClick={() => onOpenFolder?.(null)}
        className="inline-flex items-center gap-1 rounded px-1 min-h-[32px] hover:text-teal-700 transition-colors"
        data-testid="dms-breadcrumb-root">
        <House size={14} /> すべての書類
      </button>
      {path.slice(0, -1).map((f) => (
        <span key={f.id} className="flex items-center gap-1">
          <CaretRight size={11} className="text-zinc-300" />
          <button type="button" onClick={() => onOpenFolder?.(f.id)}
            data-testid={`dms-breadcrumb-${f.id}`}
            className="rounded px-1 min-h-[32px] hover:text-teal-700 transition-colors">
            {f.name}
          </button>
        </span>
      ))}
    </nav>
  );
};

/**
 * フォルダーの基本操作。フォルダー名はページ見出し（h1）が担うため、
 * ここでは件数・公開範囲・操作だけを扱います。
 */
export default function FolderBar({ folder, folders, docCount, canEdit, canManage, isAdmin,
  pinned, onOpenFolder, onAddFiles, onNewFolder, onEditFolder, onChanged }) {
  const [busy, setBusy] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [preview, setPreview] = useState(null);
  const [cascade, setCascade] = useState(false);
  if (!folder) return null;
  const isPinned = (pinned || []).includes(folder.id);

  const pin = async () => {
    setBusy(true);
    try {
      const r = await dmsApi.togglePin(folder.id);
      toast.success(r.pinned ? 'ホームにピン留めしました' : 'ピン留めを外しました');
      onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || 'ピン留めに失敗しました'); }
    finally { setBusy(false); }
  };

  const remove = async () => {
    // D7 — 取り返しのつかない操作なので、何が消えるかを数えて見せてから実行する。
    setConfirmOpen(true);
    setPreview(null);
    if (isAdmin) {
      try { setPreview(await dmsApi.folderDeletePreview(folder.id)); }
      catch (_e) { setPreview(null); }
    }
  };

  const doRemove = async () => {
    setConfirmOpen(false);
    setBusy(true);
    try {
      const out = await dmsApi.deleteFolder(folder.id, cascade);
      toast.success(out.documents_trashed
        ? `フォルダーを削除しました（サブフォルダー ${out.subfolders_deleted}個・書類 ${out.documents_trashed}件をゴミ箱へ）`
        : 'フォルダーを削除しました');
      onOpenFolder?.(null);
      onChanged?.();
    } catch (e) { toast.error(e?.response?.data?.detail || 'フォルダーを削除できませんでした'); }
    finally { setBusy(false); }
  };

  return (
    <div className="mb-5" data-testid="dms-folder-header">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <p className="flex items-center gap-2">
          <span className="text-sm text-zinc-500 tabular-nums">{docCount}件の書類</span>
          <span className="text-zinc-300">·</span>
          <AccessBadge folder={folder} />
          {isPinned && <span className="text-xs text-teal-700">ピン留め中</span>}
        </p>
        <div className="flex items-center gap-2 flex-wrap" data-testid="dms-folder-actions">
          {canEdit && <Action icon={UploadSimple} label="ファイルを追加" tone="primary"
            onClick={onAddFiles} testId="dms-folder-add-files" />}
          <Action icon={PushPin} label={isPinned ? 'ピン留めを外す' : 'ピン留め'} onClick={pin}
            testId="dms-folder-pin" />
          {canManage && <>
            <Action icon={FolderSimplePlus} label="新規フォルダー" onClick={() => onNewFolder?.(folder.id)}
              testId="dms-folder-new" />
            <Action icon={PencilSimple} label="名前を変更" onClick={() => onEditFolder?.(folder, 'rename')}
              testId="dms-folder-rename" />
            <Action icon={ArrowsOutCardinal} label="移動" onClick={() => onEditFolder?.(folder, 'move')}
              testId="dms-folder-move" />
            <Action icon={ShareNetwork} label="共有・権限" onClick={() => onEditFolder?.(folder, 'share')}
              testId="dms-folder-share" />
            <Action icon={TrashSimple} label="フォルダーを削除" tone="danger" onClick={remove}
              testId="dms-folder-delete" />
          </>}
          {busy && <span className="text-xs text-zinc-400">処理中...</span>}
        </div>
      </div>
      <ConfirmDialog open={confirmOpen} testid="dms-folder-delete-confirm"
        title="フォルダーを削除" tone="danger"
        confirmLabel={cascade ? '中身ごと削除' : 'フォルダーを削除'}
        message={`フォルダー「${folder.name}」を削除します。`}
        requireText={folder.name}
        disabled={!!preview?.blocked}
        extra={(
          <div className="rounded-md border border-zinc-200 bg-zinc-50 px-3 py-2 text-xs"
            data-testid="dms-folder-delete-preview">
            {!preview && isAdmin && <p className="text-zinc-400">中身を数えています…</p>}
            {preview && (
              <>
                <p className="text-zinc-700">
                  中身: サブフォルダー <span className="font-semibold">{preview.subfolder_count}</span>個 ・
                  書類 <span className="font-semibold">{preview.document_count}</span>件
                </p>
                {preview.subfolder_count > 0 && (
                  <p className="mt-0.5 text-zinc-500">
                    {preview.subfolders.slice(0, 6).map((f) => f.name).join('、')}
                    {preview.subfolder_count > 6 && ` 他 ${preview.subfolder_count - 6}個`}
                  </p>
                )}
                {preview.blocked_reason && (
                  <p className="mt-1 font-semibold text-rose-700">{preview.blocked_reason}</p>
                )}
                {(preview.subfolder_count > 0 || preview.document_count > 0) && !preview.blocked && (
                  <label className="mt-2 flex items-start gap-1.5 text-zinc-700">
                    <input type="checkbox" className="mt-0.5 h-3.5 w-3.5 accent-rose-600"
                      checked={cascade} onChange={(e) => setCascade(e.target.checked)}
                      data-testid="dms-folder-delete-cascade" />
                    <span>
                      <span className="font-semibold">中身ごと削除する</span>
                      （サブフォルダーも消し、書類は<span className="font-semibold">ゴミ箱</span>へ移します。
                      復元すると親フォルダーに戻ります）
                    </span>
                  </label>
                )}
              </>
            )}
            {!isAdmin && (
              <p className="text-zinc-500">
                書類やサブフォルダーが入っていると削除できません（中身ごとの削除は管理者のみ）
              </p>
            )}
          </div>
        )}
        onCancel={() => { setConfirmOpen(false); setCascade(false); }} onConfirm={doRemove} />
    </div>
  );
}
